<template>
  <div
    :dir="dir"
    class="v-select"
    :class="{
      ...stateClasses,
      'vs--open': minimizeMultiple && showAllSelected,
    }"
  >
    <div
      ref="toggle"
      @click.prevent="toggleDropdown"
      class="vs__dropdown-toggle"
      :class="{ 'vs_show-all': showAllSelected }"
      :id="id"
    >
      <div class="vs__selected-options w-88" ref="selectedOptions">
        <slot
          v-for="(option, index) in selectedValue"
          name="selected-option-container"
          :option="normalizeOptionForSlot(option)"
          :deselect="deselect"
          :multiple="multiple"
          :disabled="disabled"
        >
          <span
            class="vs__selected w-100"
            v-bind:key="option.index"
            v-if="!index || showAllSelected"
          >
            <slot
              name="selected-option"
              v-bind="normalizeOptionForSlot(option)"
            >
              <span class="text-truncate w-100 d-inline-block">{{
                getOptionLabel(option)
              }}</span>
            </slot>
            <button
              ref="deselectButtons"
              v-if="multiple"
              :disabled="disabled"
              @click="deselectOption(option)"
              type="button"
              class="vs__deselect"
              aria-label="Deselect option"
            >
              <component :is="childComponents.Deselect" />
            </button>
          </span>
        </slot>
        <slot name="show-all">
          <span
            class="vs__selected vs_plus_more"
            v-if="!showAllSelected && selectedValue.length > 1"
          >
            +{{ selectedValue.length - 1 }} more
          </span>
        </slot>
        <slot name="placeholder">
          <span class="vs__selected" v-if="!selectedValue.length">{{
            placeholder
          }}</span>
        </slot>
        <slot name="search" v-bind="scope.search" v-if="multiple">
          <input
            class="vs__search"
            v-bind="scope.search.attributes"
            v-on="scope.search.events"
          />
        </slot>
      </div>

      <div class="vs__actions">
        <button
          ref="clearButton"
          v-show="showClearButton"
          :disabled="disabled"
          @click="clearSelection"
          type="button"
          class="vs__clear"
          title="Clear selection"
        >
          <component :is="childComponents.Deselect" />
        </button>

        <slot name="open-indicator" v-bind="scope.openIndicator">
          <component
            :is="childComponents.OpenIndicator"
            v-if="!noDrop"
            v-bind="scope.openIndicator.attributes"
          />
        </slot>

        <slot name="spinner" v-bind="scope.spinner">
          <div class="vs__spinner" v-show="mutableLoading">Loading...</div>
        </slot>
      </div>
    </div>

    <transition :name="transition">
      <div class="ul-frame" v-if="dropdownOpen">
        <ul
          ref="dropdownMenu"
          class="vs__dropdown-menu with-fix-foot"
          role="listbox"
          @mousedown="onMousedown"
          @mouseup="onMouseUp"
        >
          <li
            class="vs__dropdown-option"
            v-if="multiple && minimizeMultiple && filteredOptions.length"
            @click="selectAll"
          >
            Select all
          </li>
          <li class="vs__dropdown-option" v-if="!multiple">
            <slot name="search" v-bind="scope.search">
              <input
                class="vs__search"
                v-bind="scope.search.attributes"
                v-on="scope.search.events"
                placeholder="Search..."
              />
            </slot>
          </li>
          <template v-for="(option, index) in filteredOptions">
            <slot
              name="option-top"
              v-bind="normalizeOptionForSlot(option)"
            ></slot>
            <li
              role="option"
              :key="index"
              class="vs__dropdown-option text-truncate"
              :class="{
                'vs__dropdown-option--selected': isOptionSelected(option),
                'vs__dropdown-option--highlight':
                  index === typeAheadPointer && highlight,
                'vs__dropdown-option--mapselect':
                  mappingSelect != '' && mappingSelectCheck(option.value),
              }"
              @mouseover="
                typeAheadPointer = highlight ? index : typeAheadPointer
              "
              @mousedown.prevent.stop="select(option)"
            >
              <slot name="option" v-bind="normalizeOptionForSlot(option)">
                {{ getOptionLabel(option) }}
              </slot>
            </li>
            <slot
              name="option-bottom"
              v-bind="normalizeOptionForSlot(option)"
            ></slot>
          </template>
          <li
            v-if="!filteredOptions.length"
            class="vs__no-options"
            @mousedown.stop="$emit('click-no-empty', search)"
          >
            <slot name="no-options"
              >Oops! We couldn't find any matching results</slot
            >
          </li>
          <li
            v-if="addFeildText"
            class="vs__dropdown-option footer"
            @mousedown.stop="$emit('click-no-options', search)"
          >
            <span class="btn-45">
              <img
                src="@/assets/img/icons/plus-square-primary.svg"
                width="18"
                class="mr-1"
                alt=""
              />
              {{
                search
                  ? 'Add "' +
                    search +
                    '"' +
                    (this.fieldName ? " as new " + this.fieldName : "")
                  : addFeildText
              }}
            </span>
          </li>
          <li
            v-if="search && addFeildNew"
            class="vs__dropdown-option footer"
            @mousedown.stop="$emit('click-no-options', search)"
          >
            <span class="btn-45">
              <span class="btn-45">
                Create "<b>{{ search }}</b
                >" as new tag
              </span>
            </span>
          </li>
          <slot name="validation-error"></slot>
        </ul>
      </div>
    </transition>
  </div>
</template>
<script>
import vSelect from "vue-select";

export default {
  extends: vSelect,
  // mixins: [...vSelect],
  components: {
    vSelect,
  },
  props: {
    mappingSelect: { default: "" },
    id: {
      default: "",
    },
    highlight: {
      default: true,
    },
    returnValue: {
      default: false,
    },
    minimizeMultiple: {
      default: false,
    },
    fieldName: {
      default: "",
    },
    getOptionLabel: {
      type: Function,
      default(option) {
        if (typeof option === "object") {
          if (!Object.prototype.hasOwnProperty.call(option, this.label)) {
            return console.warn("no option");
          }
          return option[this.label];
        }
        if (this.returnValue) {
          var selected = this.options.find((opt) => {
            return opt.value === option;
          });
          return selected && selected[this.label] ? selected[this.label] : "";
        }
        return option;
      },
    },
    addFeildText: {
      type: String,
      default: "",
    },
    addFeildNew: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      search: "",
      open: false,
      isComposing: false,
      pushedTags: [],
      // eslint-disable-next-line
      _value: [],
      showAllSelected: !this.minimizeMultiple,
    };
  },
  computed: {
    selectedValue() {
      let value = this.value;
      if (this.isTrackingValues) {
        value = this.$data._value;
      }
      if (value || value === 0) {
        return [].concat(value);
      }
      return [];
    },
    optionList() {
      let options = this.options.concat(this.pushTags ? this.pushedTags : []);
      if (this.multiple) {
        return options.filter((option) => !this.isOptionSelected(option));
      }
      return options;
    },
  },
  watch: {
    open() {
      setTimeout(() => {
        if (!this.open) {
          this.showAllSelected = !this.minimizeMultiple;
        }
      }, 250);
    },
  },
  methods: {
    selectAll() {
      this.updateValue([...this.selectedValue, ...this.filteredOptions]);
      this.$refs.search.focus();
    },
    deselectOption(option) {
      this.deselect(option);
      if (this.multiple && this.minimizeMultiple) {
        setTimeout(() => {
          this.$refs.search.focus();
        }, 200);
      }
    },
    isOptionSelected(option) {
      return this.selectedValue.some((value) => {
        if (
          this.multiple &&
          Object.prototype.hasOwnProperty.call(option, "value")
        ) {
          option = option.value;
        }
        return this.optionComparator(value, option);
      });
    },
    toggleDropdown(event) {
      const targetIsNotSearch = event.target !== this.$refs.search;
      if (targetIsNotSearch) {
        event.preventDefault();
      }

      //  don't react to click on deselect/clear buttons,
      //  they dropdown state will be set in their click handlers
      const ignoredButtons = [
        ...(this.$refs["deselectButtons"] || []),
        ...([this.$refs["clearButton"]] || []),
      ];

      if (
        (this.$refs.search === undefined && this.multiple) ||
        ignoredButtons
          .filter(Boolean)
          .some((ref) => ref.contains(event.target) || ref === event.target)
      ) {
        event.preventDefault();
        return;
      }

      if (this.open && targetIsNotSearch) {
        this.$refs.search.blur();
      } else if (!this.disabled) {
        this.open = true;
        this.showAllSelected = true;
        this.$nextTick(() => {
          this.$refs.search.focus();
        });
      }
    },
    maybeDeleteValue() {
      if (
        !this.$refs.search.value.length &&
        this.$refs.search &&
        !this.$refs.search.value.length &&
        this.selectedValue &&
        this.selectedValue.length &&
        this.clearable
      ) {
        let value = null;
        if (this.multiple) {
          value = [
            ...this.selectedValue.slice(0, this.selectedValue.length - 1),
          ];
        }
        this.updateValue(value);
      }
    },
    typeAheadSelect() {
      if (!this.taggable && this.filteredOptions[this.typeAheadPointer]) {
        this.select(this.filteredOptions[this.typeAheadPointer]);
      } else if (this.taggable && this.search.length) {
        this.select(this.createOption(this.search));
      }
      if (this.clearSearchOnSelect) {
        this.$emit("enter", this.search);
        this.search = "";
      }
    },
    mappingSelectCheck(option) {
      var index = this.mappingSelect.indexOf(option);
      if (index > -1) {
        return 1;
      } else {
        return 0;
      }
    },
    focus() {
      this.open = true;
      this.$nextTick(() => {
        this.$refs.search.focus();
      });
    },
    updateValue(value) {
      if (this.isTrackingValues) {
        // Vue select has to manage value
        this.$data._value = value;
      }
      if (value !== null) {
        if (Array.isArray(value)) {
          value = value.map((val) => this.reduce(val));
        } else {
          value = this.reduce(value);
        }
      }
      if (this.multiple && this.returnValue) {
        value = value.map((val) =>
          Object.prototype.hasOwnProperty.call(val, "value") ? val.value : val
        );
      } else if (
        this.returnValue &&
        value &&
        Object.prototype.hasOwnProperty.call(value, "value")
      ) {
        value = value.value;
      }
      this.$emit("input", value);
    },
  },
};
</script>
